/**
 * Canonical BizRnR plan catalogue — single source of truth for prices
 * that also appear in static prose (public/llms.txt, /ai-context,
 * README, landing copy, etc.).
 *
 * DO NOT edit a price here without also:
 *   - `npm run seo:sync-pricing`  — verify static prose (llms.txt, /ai-context)
 *   - `npm run pricing:generate`  — regenerate the global_pricing projection
 *     (commit src/lib/pricing/global-pricing.generated.json; CI `pricing:check`
 *     fails on drift) and `npm run pricing:sync -- --apply` to the DB
 *   - update Stripe price IDs (env-owned; not derived here)
 * See scripts/pricing/README.md for the full SSOT → global_pricing flow.
 */

export interface CanonicalPlan {
  id: PlanId;
  name: string;
  /** Monthly price in USD; null for "contact sales" tiers. */
  price_monthly: number | null;
  stripe_price_env: string | null;
  summary: string;
  tagline?: string;
  includedMinutes?: number | null;
  includedInteractions?: number | null;
  overageRatePerUnit?: number | null;
  features?: string[];
  notIncluded?: string[];
  highlight?: boolean;
  badge?: string | null;
  priceDisplay?: string;
  priceMin?: number;
  /**
   * On-hold / mothballed under Code Red: kept in the catalogue (existing
   * subscribers, billing/entitlements logic, SEO prose) but hidden from the
   * production pricing UI. Not deleted — restored when traction justifies it.
   */
  onHold?: boolean;
}

export type PlanId = 'spark' | 'ignite' | 'inferno' | 'enterprise';

export const PLANS: readonly CanonicalPlan[] = [
  {
    id: 'spark',
    // User-facing label. Internal id stays 'spark' (Stripe/DB/entitlements key).
    name: 'AI Receptionist',
    price_monthly: 99,
    stripe_price_env: 'STRIPE_PRICE_SPARK',
    summary: 'Your AI front office — answers every call, never misses a lead',
    tagline: 'AI voice receptionist for solopreneurs',
    // includedInteractions is the canonical billed unit; includedMinutes kept
    // for legacy/back-compat only.
    includedMinutes: 100,
    includedInteractions: 100,
    overageRatePerUnit: 0.20,
    // Code Red locked scope: voice answering + dedicated number + post-call SMS
    // briefing (+ missed-call text-back), now also including the AI Business
    // Page (AEO-indexed mini-site) and CRM Lite (lead capture) — both built and
    // marketed on the public Spark card. Web chat remains built-but-DEFERRED.
    features: [
      '100 AI interactions/mo',
      '1 dedicated phone number',
      'Custom receptionist name & voice',
      'AI Business Page (AEO-indexed for AI search)',
      'CRM Lite — capture every lead',
      'Missed-call text-back',
      'Post-call SMS briefing to owner',
      'Receptionist call dashboard',
      'Transcripts and caller history',
      'Forwarding setup quest',
    ],
    notIncluded: [
      'Web chat widget',
      'Outbound calls',
      'SMS campaigns',
      'WhatsApp / social DMs',
      'Lead gen pipeline',
    ],
    highlight: false,
    badge: null,
  },
  {
    id: 'ignite',
    name: 'Ignite',
    price_monthly: 999,
    stripe_price_env: 'STRIPE_PRICE_IGNITE',
    summary: 'Full lead gen + multi-channel automation',
    tagline: 'The complete AI business engine',
    includedMinutes: 2000,
    includedInteractions: 2000,
    overageRatePerUnit: 0.10,
    features: [
      'Everything in the AI Receptionist plan',
      '2,000 pooled interactions/mo (voice + SMS + WhatsApp + chat)',
      'Inbound WhatsApp AI agent (reply within 24h)',
      'Instagram + Facebook DM AI',
      'AI review responses (Google + Yelp)',
      'ERM lead gen pipeline (2,400 leads/day)',
      'AI email outreach',
      'Full CRM + deal pipeline',
      'Remove "Powered by" badge',
    ],
    notIncluded: [
      'Outbound AI calling',
      'SMS campaigns',
      'Outbound WhatsApp templates',
    ],
    highlight: false,
    badge: null,
    onHold: true,
  },
  {
    id: 'inferno',
    name: 'Inferno',
    price_monthly: 4999,
    stripe_price_env: 'STRIPE_PRICE_INFERNO',
    summary: 'Omnichannel AI automation at scale',
    tagline: 'Multi-location AI domination',
    includedMinutes: 20000,
    includedInteractions: 20000,
    overageRatePerUnit: 0.05,
    features: [
      'Everything in Ignite',
      '20,000 pooled interactions/mo',
      'Outbound AI calling — reach warm leads with one click',
      'SMS campaigns + sequences — multi-touch automated follow-up',
      'Outbound WhatsApp campaigns (template-based)',
      'TikTok + LinkedIn + X DM AI',
      'AI social post scheduling',
      'Multi-location (up to 5)',
      '5 team seats',
      'White-label + own domain',
      'Zapier / Make / n8n integrations',
      'API access',
      'Custom agent voices',
      'Priority support',
    ],
    highlight: false,
    badge: null,
    onHold: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price_monthly: null,
    priceDisplay: 'Custom',
    priceMin: 10000,
    stripe_price_env: null,
    summary: 'More than a receptionist — full multi-channel AI automation, custom-built for your business',
    tagline: 'AI automation across voice, email, social, and appointments',
    includedMinutes: null,
    includedInteractions: null,
    overageRatePerUnit: null,
    features: [
      'Everything in the AI Receptionist plan, plus multi-channel automation (email, social DMs, appointments, and more)',
      'Unlimited concurrent AI agents',
      'Contracted interaction volume',
      'Call routing + IVR replacement',
      'Salesforce + HubSpot integration',
      'Compliance recording + PII redaction',
      'Real-time supervision dashboard',
      'Human escalation routing',
      'Unlimited team seats + SSO',
      'Dedicated AI success manager',
      'SLA + uptime guarantee',
    ],
    highlight: false,
    badge: 'Contact sales',
  },
] as const;

/** Convenience: plans that should show a dollar amount in prose. */
export const PAID_PLANS = PLANS.filter(
  (p): p is CanonicalPlan & { price_monthly: number } =>
    typeof p.price_monthly === 'number' && p.price_monthly > 0,
);

/**
 * Plans shown in the production pricing UI. Excludes on-hold/mothballed tiers
 * (Ignite/Inferno under Code Red) while keeping them in PLANS for existing
 * subscribers, billing/entitlements, and SEO prose.
 */
export const VISIBLE_PLANS = PLANS.filter((p) => !p.onHold);

/** Format a plan's monthly price for prose: "$99/mo". */
export function formatPlanPrice(plan: CanonicalPlan): string {
  if (plan.price_monthly === null) return plan.priceDisplay ?? 'custom';
  if (plan.price_monthly === 0) return '$0/mo';
  return `$${plan.price_monthly.toLocaleString()}/mo`;
}

/**
 * Look up a plan by id. Throws if the id is unknown — callers pass a literal
 * PlanId, so a miss is a programming error, not a runtime condition.
 */
export function getPlan(id: PlanId): CanonicalPlan {
  const plan = PLANS.find((p) => p.id === id);
  if (!plan) throw new Error(`Unknown plan id: ${id}`);
  return plan;
}

/** The Spark plan — the self-serve entry tier. The marketing UI references
 *  Spark's price/clause constantly; centralize them here so a price change in
 *  PLANS propagates instead of leaving stale literals scattered in copy. */
export const SPARK = getPlan('spark');

/** Canonical Enterprise plan (Custom pricing, from priceMin) for UI + prose. */
export const ENTERPRISE = getPlan('enterprise');

/** Canonical Spark price label for UI + prose: "$99/mo". */
export const SPARK_PRICE = formatPlanPrice(SPARK);

/** Canonical Spark trust clause reused across pricing surfaces. */
export const SPARK_TRUST_CLAUSE = `${SPARK_PRICE} · cancel anytime · 30-day money-back guarantee`;

/**
 * Map a stored plan identifier (any historical alias) to its user-facing label.
 * Single source of truth for plan badges/labels across the dashboard — keeps the
 * Spark→"AI Receptionist" rename from drifting across copies of this map.
 *
 * Internal ids stay 'spark'/'ignite'/'inferno'/'enterprise'; only the display
 * string changes. Legacy aliases (starter/professional/pro/custom) and the
 * Ignite/Inferno labels are vestigial admin cosmetics (≈0 such subscribers) but
 * kept so existing/legacy rows still render a sensible badge.
 */
const PLAN_DISPLAY_NAMES: Record<string, string> = {
  trial: 'Activation',
  spark: 'AI Receptionist',
  starter: 'AI Receptionist',
  ignite: 'Ignite',
  professional: 'Ignite',
  pro: 'Ignite',
  inferno: 'Inferno',
  enterprise: 'Enterprise',
  custom: 'Enterprise',
};

export function planDisplayName(plan: string | null | undefined): string {
  const key = (plan ?? '').toLowerCase();
  return (
    PLAN_DISPLAY_NAMES[key] ?? (key ? key.charAt(0).toUpperCase() + key.slice(1) : 'Unknown')
  );
}

/**
 * SEO priceRange across the production-visible catalogue (VISIBLE_PLANS, i.e.
 * excludes on-hold Ignite/Inferno so JSON-LD priceRange stops emitting $4999).
 * Visible tiers are Spark ($99) + Enterprise (custom/no numeric price), so the
 * range collapses to a "from" label, e.g. "$99/month" or "From $99/month".
 */
export function pricingRangeLabel(): string {
  const prices = VISIBLE_PLANS
    .map((p) => p.price_monthly)
    .filter((p): p is number => typeof p === 'number' && p > 0);
  if (prices.length === 0) return 'Custom pricing';
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? `From $${min}/month` : `$${min}-$${max}/month`;
}
